<template>
  <div class="confirmation-page">

    <!-- Success icon -->
    <div class="confirmation-icon">
      <Icon name="check" :size="36" color="var(--primary)" :stroke="3" />
    </div>

    <h1 class="confirmation-title">Pedido levantado</h1>

    <p class="confirmation-subtitle">
      {{ perComedor.length }} orden{{ perComedor.length !== 1 ? 'es' : '' }} enviada{{ perComedor.length !== 1 ? 's' : '' }} correctamente.
    </p>

    <!-- Order list -->
    <div class="order-summary">
      <div
        v-for="({ cid, lines, total }) in perComedor"
        :key="cid"
        class="order-row"
      >
        <div>
          <div class="order-row__name">{{ getComedorById(cid)?.name }}</div>
          <div class="order-row__meta">{{ lines.length }} artículos · Ruta {{ getComedorById(cid)?.ruta }}</div>
        </div>

        <div class="order-row__status">
          <Pill bg="var(--bg)" color="var(--text-2)">
            <span class="status-preparing-dot" />
            Preparando
          </Pill>
        </div>

        <div class="order-row__total">{{ money(total) }}</div>
      </div>

      <!-- Grand total row -->
      <div class="order-grand-total">
        <span class="order-grand-total__label">Total</span>
        <span class="order-grand-total__amount">{{ money(grand) }}</span>
      </div>
    </div>

    <!-- Actions -->
    <div class="confirmation-actions">
      <button @click="$emit('back-home')" class="btn-secondary">
        <Icon name="receipt" :size="14" color="var(--primary)" /> Ver mis ordenes
      </button>
      <button @click="$emit('new-order')" class="btn-primary">
        <Icon name="plus" :size="14" color="#fff" /> Levantar otro pedido
      </button>
    </div>

  </div>
</template>

<script setup>
import { computed } from 'vue'
import Icon from '@/components/ui/Icon.vue'
import Pill from '@/components/ui/Pill.vue'

const props = defineProps({
  activeIds: { type: Array,  default: () => [] },
  cart:      { type: Object, default: () => ({}) },
  store:     { type: Object, default: null },
})

function getProductById(id) { return props.store?.getProductById(id) || null }
function getComedorById(id) { return props.store?.getCenterById(id)  || null }

defineEmits(['back-home', 'new-order'])

function money(n) {
  return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const perComedor = computed(() =>
  props.activeIds
    .map(cid => {
      const lines = Object.entries(props.cart[cid] || {}).filter(([, q]) => q > 0)
      const total = lines.reduce((s, [pid, q]) => s + (getProductById(pid)?.price || 0) * q, 0)
      return { cid, lines, total }
    })
    .filter(p => p.lines.length > 0)
)

const grand = computed(() => perComedor.value.reduce((s, p) => s + p.total, 0))
</script>

<style scoped>
/* ─── Page ───────────────────────────────────────────────────────────────────── */
.confirmation-page {
  padding: 48px;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}

/* ─── Success icon ───────────────────────────────────────────────────────────── */
.confirmation-icon {
  width: 80px;
  height: 80px;
  border-radius: 999px;
  background: var(--primary-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.confirmation-title {
  margin: 0;
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 30px;
  color: var(--text);
  letter-spacing: -0.5px;
}

.confirmation-subtitle {
  margin: 8px 0 0;
  font-family: var(--ff-body);
  font-size: 15px;
  color: var(--text-2);
}

/* ─── Order summary card ─────────────────────────────────────────────────────── */
.order-summary {
  margin-top: 30px;
  padding: 22px;
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  text-align: left;
}

/* ─── Order row ──────────────────────────────────────────────────────────────── */
.order-row {
  padding: 14px 0;
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: 1fr 110px 100px;
  align-items: center;
  gap: 12px;
}

.order-row:last-of-type {
  border-bottom: none;
}

.order-row__name {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  color: var(--text);
}

.order-row__meta {
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text-2);
  margin-top: 2px;
}

.order-row__status {
  display: flex;
  justify-content: center;
}

.status-preparing-dot {
  width: 6px;
  height: 6px;
  background: var(--text-2);
  border-radius: 999px;
  display: inline-block;
}

.order-row__total {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 16px;
  color: var(--text);
  text-align: right;
}

/* ─── Grand total ────────────────────────────────────────────────────────────── */
.order-grand-total {
  padding-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.order-grand-total__label {
  font-family: var(--ff-body);
  font-size: 14px;
  color: var(--text-2);
}

.order-grand-total__amount {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 22px;
  color: var(--text);
}

/* ─── Action buttons ─────────────────────────────────────────────────────────── */
.confirmation-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 22px;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  background: var(--surface);
  color: var(--primary);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 14px;
  box-shadow: inset 0 0 0 1px var(--primary);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 14px;
}
</style>
